import { useState, useEffect, useCallback, useRef } from "react";
import { supabase, isSupabaseConfigured } from "./lib/supabaseClient";
import { useAuth } from "./lib/useAuth";
import { siteRedirectUrl, startOAuthSignIn } from "./lib/authCallback";
import { loadStripe, stripeConfigured } from "./lib/stripeJs";
import {
  validateEmail,
  validatePassword,
  passwordsMatch,
  NEUTRAL_RESET_MESSAGE,
  friendlyAuthError,
} from "./lib/authValidation";

// Human-readable labels for the optional service add-on.
const ADDON_LABELS = { weekend: "Weekend Service (+$50)", holiday: "Holiday Service (+$100)", priority: "Priority — live updates at each attempt (+$75)" };

function StatusPill({ status }) {
  const labels = {
    pending_review: "Pending Review",
    accepted: "Accepted / In Service",
    awaiting_client: "Awaiting Your Response",
    completed: "Service Completed",
    cancelled: "Cancelled",
    refund_issued: "Refund Issued",
  };
  return <span className={`status-chip status-${status}`}>{labels[status] || status || "—"}</span>;
}

// Where a provider sends the browser back to: THIS site. A deploy preview
// returns to the preview and production returns to production — no deployment
// URL is ever written into the code. Resolved once, from the live origin.
const redirectBase = siteRedirectUrl();

export default function ClientAccount({ onNewOrder }) {
  const { user, accessToken, loading, recovery, clearRecovery, authError } = useAuth();

  if (!isSupabaseConfigured) {
    return (
      <div className="alert alert-warn">
        Client sign-in is temporarily unavailable. Please try again later or contact us directly.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="ai-loading" style={{ padding: "1rem" }}>
        <div className="spinner" /> Loading…
      </div>
    );
  }
  if (recovery) {
    return <ResetPassword onDone={clearRecovery} />;
  }
  if (user) {
    return <AccountPortal user={user} accessToken={accessToken} onNewOrder={onNewOrder} />;
  }
  // A sign-in that came back but could not be completed says so here, rather
  // than leaving the login form looking as though nothing happened.
  return <AuthScreens authError={authError} />;
}

// ── Logged-out: login / signup / forgot ─────────────────────────────────────────
function AuthScreens({ authError }) {
  const [view, setView] = useState("login"); // login | signup | forgot
  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      {authError && <div className="alert alert-warn" role="status" style={{ marginBottom: "0.75rem" }}>{authError}</div>}
      {view === "login" && <LoginView setView={setView} />}
      {view === "signup" && <SignUpView setView={setView} />}
      {view === "forgot" && <ForgotView setView={setView} />}
    </div>
  );
}

function OAuthButtons({ setError, setBusy, busy }) {
  async function oauth(provider) {
    setError("");
    setBusy(true);
    // startOAuthSignIn holds a latch OUTSIDE React. `disabled={busy}` cannot
    // stop a second click on its own — setBusy is asynchronous, so two clicks in
    // the same batch both run before anything re-renders — and a second
    // signInWithOAuth mints a fresh PKCE verifier over the one belonging to the
    // flow already in flight. The provider then returns a code the stored
    // verifier does not match, and the sign-in cannot be completed.
    const r = await startOAuthSignIn(supabase, provider, redirectBase);
    if (!r.started) {
      if (r.reason === "already_in_progress") return;   // the first click is working
      setError(friendlyAuthError(r.error));
      setBusy(false);
    }
    // On success the browser redirects to the provider; no further action here.
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
      <button className="btn-secondary" disabled={busy} onClick={() => oauth("google")}
        style={{ padding: "0.7rem", fontWeight: 700 }}>
        Continue with Google
      </button>
      <button className="btn-secondary" disabled={busy} onClick={() => oauth("apple")}
        style={{ padding: "0.7rem", fontWeight: 700 }}>
        Continue with Apple
      </button>
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", margin: "1rem 0", color: "var(--gray)", fontSize: "0.8rem" }}>
      <div style={{ flex: 1, height: 1, background: "var(--light)" }} />
      or
      <div style={{ flex: 1, height: 1, background: "var(--light)" }} />
    </div>
  );
}

function LoginView({ setView }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function signIn(e) {
    e.preventDefault();
    setError("");
    if (!validateEmail(email)) return setError("Please enter a valid email address.");
    if (!password) return setError("Please enter your password.");
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (error) setError(friendlyAuthError(error));
      // On success useAuth flips to the account portal automatically.
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, marginBottom: "1rem" }}>Client Login</h3>
      <OAuthButtons setError={setError} setBusy={setBusy} busy={busy} />
      <Divider />
      <form onSubmit={signIn}>
        <div className="field" style={{ marginBottom: "0.75rem" }}>
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
        </div>
        <div className="field" style={{ marginBottom: "0.75rem" }}>
          <label>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </div>
        {error && <div className="alert alert-warn" style={{ marginBottom: "0.75rem" }}>{error}</div>}
        <button className="btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem", fontSize: "0.82rem" }}>
        <span style={{ color: "var(--navy)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => setView("signup")}>Create Account</span>
        <span style={{ color: "var(--navy)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => setView("forgot")}>Forgot Password?</span>
      </div>
    </div>
  );
}

function SignUpView({ setView }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  async function createAccount(e) {
    e.preventDefault();
    setError("");
    setNotice("");
    if (!fullName.trim()) return setError("Please enter your full name.");
    if (!validateEmail(email)) return setError("Please enter a valid email address.");
    if (!validatePassword(password)) return setError("Please choose a password with at least 8 characters.");
    if (!passwordsMatch(password, confirm)) return setError("The passwords do not match.");
    if (!agree) return setError("Please agree to the Terms to continue.");
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { data: { full_name: fullName.trim() }, emailRedirectTo: redirectBase },
      });
      if (error) {
        setError(friendlyAuthError(error));
      } else if (data && data.session) {
        // Email confirmation disabled — the user is signed in immediately.
        setNotice("Account created. You're signed in.");
      } else {
        // Email confirmation enabled — a confirmation link was sent.
        setNotice("Check your email to confirm your account, then sign in.");
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, marginBottom: "1rem" }}>Create Account</h3>
      <form onSubmit={createAccount}>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>Full Name</label>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" /></div>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></div>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></div>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>Confirm Password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", margin: "0.5rem 0 0.75rem" }}>
          <input type="checkbox" checked={agree} onChange={() => setAgree(!agree)} />
          I agree to the Service Agreement / Terms
        </label>
        {error && <div className="alert alert-warn" style={{ marginBottom: "0.75rem" }}>{error}</div>}
        {notice && <div className="alert" style={{ marginBottom: "0.75rem" }}>{notice}</div>}
        <button className="btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Creating…" : "Create Account"}
        </button>
      </form>
      <div style={{ marginTop: "1rem", fontSize: "0.82rem" }}>
        Already have an account? <span style={{ color: "var(--navy)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => setView("login")}>Sign In</span>
      </div>
    </div>
  );
}

function ForgotView({ setView }) {
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function sendReset(e) {
    e.preventDefault();
    setError("");
    if (!validateEmail(email)) return setError("Please enter a valid email address.");
    setBusy(true);
    try {
      // Ignore the result to avoid revealing whether the email is registered.
      await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: redirectBase });
    } catch (err) {
      // Intentionally swallowed — always show the neutral message.
    } finally {
      setBusy(false);
      setNotice(NEUTRAL_RESET_MESSAGE);
    }
  }

  return (
    <div>
      <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, marginBottom: "1rem" }}>Reset Password</h3>
      <form onSubmit={sendReset}>
        <div className="field" style={{ marginBottom: "0.75rem" }}><label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></div>
        {error && <div className="alert alert-warn" style={{ marginBottom: "0.75rem" }}>{error}</div>}
        {notice && <div className="alert" style={{ marginBottom: "0.75rem" }}>{notice}</div>}
        <button className="btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Sending…" : "Send Reset Instructions"}
        </button>
      </form>
      <div style={{ marginTop: "1rem", fontSize: "0.82rem" }}>
        <span style={{ color: "var(--navy)", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }} onClick={() => setView("login")}>Back to Sign In</span>
      </div>
    </div>
  );
}

// Shown after a password-reset link returns (PASSWORD_RECOVERY event).
function ResetPassword({ onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function save(e) {
    e.preventDefault();
    setError("");
    if (!validatePassword(password)) return setError("Please choose a password with at least 8 characters.");
    if (!passwordsMatch(password, confirm)) return setError("The passwords do not match.");
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setError(friendlyAuthError(error));
        setBusy(false);
        return;
      }
      await supabase.auth.signOut();
      onDone();
    } catch (err) {
      setError(friendlyAuthError(err));
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 420, margin: "0 auto" }}>
      <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, marginBottom: "1rem" }}>Set a New Password</h3>
      <form onSubmit={save}>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>New Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></div>
        <div className="field" style={{ marginBottom: "0.6rem" }}><label>Confirm New Password</label>
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
        {error && <div className="alert alert-warn" style={{ marginBottom: "0.75rem" }}>{error}</div>}
        <button className="btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>
          {busy ? "Saving…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}

// ── Logged-in: account portal + My Orders ───────────────────────────────────────
//
// The account is the customer's record of their own matters: what they ordered,
// what has happened, what they were charged, and the paperwork they may keep. It
// deliberately shows LESS than the admin view — a service attempt reaches this
// screen already sanitized by the backend, and a Proof only appears once a
// person has approved it.

const money = (cents) => (cents == null ? "—" : `$${(Math.round(cents) / 100).toFixed(2)}`);

const PAYMENT_LABELS = {
  paid: "Paid", awaiting_payment: "Awaiting Payment", unpaid: "Unpaid", billed_externally: "Billed Separately",
  partially_refunded: "Partially Refunded", refunded: "Refunded", disputed: "Disputed",
};
const INVOICE_LABELS = { draft: "Draft", sent: "Sent", paid: "Paid", overdue: "Overdue", voided: "Voided", open: "Open" };
const shortDate = (v) => (v ? new Date(v).toLocaleDateString() : null);

// Current work vs finished record. A completed or archived order is still the
// customer's — it is grouped, never hidden.
function isFinished(o) {
  return Boolean(o.completedAt || o.cancelledAt || o.archivedAt)
    || o.status === "completed" || o.status === "cancelled";
}

function AccountPortal({ user, accessToken, onNewOrder }) {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);
  const name = (user.user_metadata && (user.user_metadata.full_name || user.user_metadata.name)) || user.email;

  const active = useRef(true);
  useEffect(() => () => { active.current = false; }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch("/.netlify/functions/get-my-orders", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error("load failed");
      const data = await res.json();
      if (active.current) setOrders(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (e) {
      if (active.current) setError("We couldn't load your orders right now. Please try again shortly.");
    }
  }, [accessToken]);

  useEffect(() => { if (accessToken) loadOrders(); }, [accessToken, loadOrders]);

  async function signOut() { await supabase.auth.signOut(); }

  const current = (orders || []).filter((o) => !isFinished(o));
  const past = (orders || []).filter(isFinished);

  const section = (title, list) => (
    list.length > 0 && (
      <>
        <h4 style={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", margin: "1.25rem 0 0.75rem" }}>
          {title} <span style={{ fontWeight: 500 }}>({list.length})</span>
        </h4>
        {list.map((o) => (
          <OrderCard key={o.id} order={o} accessToken={accessToken} onLinked={loadOrders}
            open={openId === o.id} onToggle={() => setOpenId(openId === o.id ? null : o.id)} />
        ))}
      </>
    )
  );

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800 }}>Welcome{name ? `, ${name}` : ""}</h3>
          <p className="text-sm">{user.email}</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button className="btn-primary" onClick={() => onNewOrder && onNewOrder()}>New Order</button>
          <button className="btn-secondary" onClick={signOut}>Sign Out</button>
        </div>
      </div>

      {error && <div className="alert alert-warn">{error}</div>}
      {!error && orders === null && (
        <div className="ai-loading"><div className="spinner" /> Loading your orders…</div>
      )}
      {!error && orders && orders.length === 0 && (
        <div className="alert">You don't have any orders yet. Click <strong>New Order</strong> to get started.</div>
      )}

      {section("Current Orders", current)}
      {section("Completed & Past Orders", past)}

      <PaymentMethods accessToken={accessToken} />
    </div>
  );
}

// ── One order ─────────────────────────────────────────────────────────────────
function OrderCard({ order: o, accessToken, open, onToggle, onLinked }) {
  const b = o.billing || {};
  return (
    <div className={`job-card${open ? " selected" : ""}`} onClick={onToggle}>
      <div className="job-card-header">
        <span className="job-id">{o.id}</span>
        <span style={{ display: "flex", gap: "0.4rem", alignItems: "center", flexWrap: "wrap" }}>
          {b.paymentStatus && (
            <span className="status-chip" style={{ background: b.paymentStatus === "paid" ? "#EFF8F3" : "#FEF9EE", color: b.paymentStatus === "paid" ? "var(--green)" : "#92400E" }}>
              {PAYMENT_LABELS[b.paymentStatus] || b.paymentStatus}
            </span>
          )}
          <StatusPill status={o.status} />
        </span>
      </div>
      <div className="job-meta">
        <div><strong>Person to Serve:</strong> {o.defendant || "—"}</div>
        <div><strong>Address:</strong> {o.address || "—"}</div>
        <div>
          <strong>Type:</strong> {o.docType || "—"} &nbsp;|&nbsp;
          <strong> Tier:</strong> {o.tier || "—"}
          {o.addon && <> &nbsp;|&nbsp; <strong>Add-On:</strong> {ADDON_LABELS[o.addon] || o.addon}</>} &nbsp;|&nbsp;
          <strong> Total:</strong> {b.totalCents != null ? money(b.totalCents) : (o.price != null ? `$${o.price}` : "—")}
        </div>
        <div>
          <strong>Order Date:</strong> {shortDate(o.submitted) || "—"}
          {o.completedAt && <> &nbsp;|&nbsp; <strong>Completed:</strong> {shortDate(o.completedAt)}</>}
        </div>
      </div>

      <LinkToAccount order={o} accessToken={accessToken} onLinked={onLinked} />

      {!open && <div className="text-sm" style={{ marginTop: "0.4rem", color: "var(--navy)", fontWeight: 600 }}>View details ▾</div>}

      {open && (
        <div style={{ marginTop: "0.75rem", borderTop: "1px solid var(--light)", paddingTop: "0.75rem" }} onClick={(e) => e.stopPropagation()}>
          <OrderDetails order={o} />
          <ServiceHistory attempts={o.serviceAttempts} />
          <SourceDocuments documents={o.documents} />
          <CompletedDocuments order={o} accessToken={accessToken} />
          <BillingSection order={o} accessToken={accessToken} />
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: "1rem" }}>
      <div style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--gold)", marginBottom: "0.4rem" }}>{title}</div>
      {children}
    </div>
  );
}

function OrderDetails({ order: o }) {
  const row = (label, value) => (value ? <div className="text-sm"><strong>{label}:</strong> {value}</div> : null);
  return (
    <Section title="Order Details">
      {row("Plaintiff / Petitioner", o.plaintiff)}
      {row("Person to Serve", o.defendant)}
      {row("Case No.", o.caseNumber)}
      {row("Court", o.court)}
      {row("Service Address", o.address)}
      {row("Document Type", o.docType)}
      {row("Service Tier", o.tier)}
      {row("Needed By", shortDate(o.neededBy))}
      {o.archivedAt && <div className="text-sm" style={{ color: "var(--gray)" }}>This order has been archived. Your records remain available here.</div>}
    </Section>
  );
}

// The backend already returns attempts stripped to a client-safe projection (no
// internal notes, no server identity). This renders exactly that and adds
// nothing — the sanitizer is the authority on what a client may read.
function ServiceHistory({ attempts }) {
  const list = Array.isArray(attempts) ? attempts : [];
  return (
    <Section title="Service Status & History">
      {list.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--gray)" }}>No service activity has been recorded yet. Each attempt will appear here as it happens.</div>
      ) : (
        <div className="note-log" data-testid="service-history">
          {list.map((a, i) => (
            <div key={i} className="note-entry">
              <div className="note-status">{a.outcomeLabel || a.outcome || "Attempt"}</div>
              <div className="note-meta">
                {a.attemptNumber ? `Attempt ${a.attemptNumber}` : "Attempt"}
                {a.date ? ` — ${shortDate(a.date)}` : ""}{a.time ? ` at ${a.time}` : ""}
              </div>
              {a.summary && <div>{a.summary}</div>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// What the CLIENT supplied for service. Never mixed with completed paperwork.
function SourceDocuments({ documents }) {
  const list = Array.isArray(documents) ? documents : [];
  return (
    <Section title="Service Documents">
      {list.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--gray)" }}>No documents are attached to this order.</div>
      ) : (
        <div className="text-sm" data-testid="source-documents">
          {list.map((d, i) => (
            <div key={i} style={{ padding: "0.1rem 0" }}>
              <a href={d.url} target="_blank" rel="noreferrer">{d.name}</a>
            </div>
          ))}
        </div>
      )}
      <div className="text-sm" style={{ color: "var(--gray)", marginTop: "0.25rem" }}>The documents supplied to be served.</div>
    </Section>
  );
}

// Approved, signed paperwork. The list carries no URLs — each file is requested
// individually so the link is minted only when the customer actually asks, and
// only after the server re-checks that this order and this document are theirs.
function CompletedDocuments({ order: o, accessToken }) {
  const list = Array.isArray(o.completedDocuments) ? o.completedDocuments : [];
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  async function openDoc(doc) {
    if (busyId) return;
    setBusyId(doc.id);
    setErr("");
    try {
      const res = await fetch("/.netlify/functions/client-order-document", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: "document", jobNumber: o.id, documentId: doc.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) window.open(data.url, "_blank", "noopener");
      else setErr(data.error || "That document couldn't be opened. Please try again.");
    } catch (e) {
      setErr("That document couldn't be opened. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Section title="Completed Documents">
      {list.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--gray)" }}>Your completed proof of service will appear here once it has been signed and reviewed.</div>
      ) : (
        <div className="text-sm" data-testid="completed-documents">
          {list.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.15rem 0", flexWrap: "wrap" }}>
              <span><strong>{d.label}</strong>{d.signedAt ? ` — signed ${shortDate(d.signedAt)}` : ""}</span>
              <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }}
                disabled={busyId === d.id} onClick={() => openDoc(d)}>
                {busyId === d.id ? "Opening…" : "View / Download"}
              </button>
            </div>
          ))}
        </div>
      )}
      {err && <div className="alert alert-warn" style={{ marginTop: "0.4rem" }}>{err}</div>}
    </Section>
  );
}

function BillingSection({ order: o, accessToken }) {
  const b = o.billing || {};
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [err, setErr] = useState("");

  async function openReceipt() {
    if (receiptBusy) return;
    setReceiptBusy(true);
    setErr("");
    try {
      const res = await fetch("/.netlify/functions/client-order-document", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: "receipt", jobNumber: o.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.receiptUrl) window.open(data.receiptUrl, "_blank", "noopener");
      else setErr(data.error || "The receipt couldn't be retrieved right now.");
    } catch (e) {
      setErr("The receipt couldn't be retrieved right now.");
    } finally {
      setReceiptBusy(false);
    }
  }

  const line = (label, value, strong) => (
    <div className="text-sm" style={{ display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.1rem 0" }}>
      <span>{label}</span><span style={{ fontWeight: strong ? 700 : 500 }}>{value}</span>
    </div>
  );

  return (
    <Section title="Billing">
      <div data-testid="billing" style={{ maxWidth: 340 }}>
        {line("Order Total", b.totalCents != null ? money(b.totalCents) : (o.price != null ? `$${o.price}` : "—"), true)}
        {line("Paid", money(b.paidCents))}
        {b.refundedCents > 0 && line("Refunded", money(b.refundedCents))}
        {line("Balance Due", b.balanceCents != null ? money(b.balanceCents) : "—", true)}
        {line("Payment Status", PAYMENT_LABELS[b.paymentStatus] || b.paymentStatus || "—")}
        {b.paidAt && line("Paid On", shortDate(b.paidAt))}
        {b.invoiceStatus && line("Invoice", INVOICE_LABELS[b.invoiceStatus] || b.invoiceStatus)}
      </div>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
        {b.invoiceUrl && (
          <a className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem", textDecoration: "none" }}
            href={b.invoiceUrl} target="_blank" rel="noreferrer">View Invoice</a>
        )}
        {b.receiptAvailable && (
          <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem" }}
            disabled={receiptBusy} onClick={openReceipt}>{receiptBusy ? "Opening…" : "View Receipt"}</button>
        )}
      </div>
      {err && <div className="alert alert-warn" style={{ marginTop: "0.4rem" }}>{err}</div>}
    </Section>
  );
}

// ── Linking an order placed before the account existed ────────────────────────
//
// An order the customer placed as a guest carries no owner. They can see it —
// the confirmed email on the account matches the address it was placed with —
// but that is the only thing connecting them, and it stops working if the
// address on the account ever changes. Linking writes the account onto the
// order, permanently.
//
// Shown ONLY on orders the server reported as fallback-owned, and never acts on
// its own: the customer presses the button. The server re-checks everything for
// itself, so this is a request, not an instruction.
function LinkToAccount({ order: o, accessToken, onLinked }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (o.ownership !== "verified_email_fallback") return null;

  async function link(e) {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch("/.netlify/functions/client-claim-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: "claim", jobNumber: o.id }),
      });
      const data = await res.json().catch(() => ({}));
      // The refreshed order list is what makes the button disappear — the
      // backend reports the new ownership, this component never assumes it.
      if (res.ok && data.ok) { if (onLinked) await onLinked(); }
      else setErr(data.error || "That order couldn't be added to your account.");
    } catch (e2) {
      setErr("That order couldn't be added to your account. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="alert" data-testid="link-to-account"
      style={{ marginTop: "0.6rem", padding: "0.6rem 0.75rem" }} onClick={(e) => e.stopPropagation()}>
      <div className="text-sm" style={{ marginBottom: "0.4rem" }}>
        You placed this order before creating your account. It's shown here because it was placed
        with your confirmed email address — add it to your account to keep it linked permanently.
      </div>
      <button className="btn-primary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.7rem" }}
        disabled={busy} onClick={link}>
        {busy ? "Adding…" : "Link to My Account"}
      </button>
      {err && <div className="text-sm" style={{ color: "var(--red)", marginTop: "0.35rem" }}>{err}</div>}
    </div>
  );
}

// ── Saved payment methods ─────────────────────────────────────────────────────
//
// Entirely optional. A client who never opens this section never gets a Stripe
// Customer, and a card is only ever saved because they asked.
//
// The card itself is typed into Stripe's own iframe and posted straight to
// Stripe with a SetupIntent client secret. Nothing in this component — or
// anywhere behind it — ever sees a number or a CVC; what comes back is a brand,
// the last four digits and an expiry.
const CARD_BRANDS = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express",
  discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
};
const WALLETS = { apple_pay: "Apple Pay", google_pay: "Google Pay", link: "Link" };

function describeCard(c) {
  const brand = CARD_BRANDS[c.brand] || (c.brand ? c.brand.replace(/\b\w/g, (m) => m.toUpperCase()) : "Card");
  const wallet = c.wallet ? ` · ${WALLETS[c.wallet] || c.wallet}` : "";
  const exp = c.expMonth && c.expYear ? ` · expires ${String(c.expMonth).padStart(2, "0")}/${String(c.expYear).slice(-2)}` : "";
  return `${brand} ending ${c.last4 || "••••"}${exp}${wallet}`;
}

function PaymentMethods({ accessToken }) {
  const [cards, setCards] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);

  const call = useCallback(async (payload) => {
    const res = await fetch("/.netlify/functions/client-payment-methods", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data.ok, data };
  }, [accessToken]);

  const load = useCallback(async () => {
    const r = await call({ action: "list" });
    if (r.ok) setCards(r.data.cards || []);
    else { setCards([]); setErr(r.data.error || "We couldn't load your saved cards."); }
  }, [call]);

  useEffect(() => { if (accessToken) load(); }, [accessToken, load]);

  async function act(action, paymentMethodId) {
    if (busy) return;
    setBusy(paymentMethodId + action);
    setErr("");
    const r = await call({ action, paymentMethodId });
    if (r.ok) setCards(r.data.cards || []);
    else setErr(r.data.error || "That didn't work. Please try again.");
    setBusy("");
  }

  return (
    <div style={{ marginTop: "2rem" }} data-testid="payment-methods">
      <h4 style={{ fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--gray)", marginBottom: "0.75rem" }}>
        Payment Methods
      </h4>

      {cards === null && <div className="ai-loading"><div className="spinner" /> Loading…</div>}

      {cards && cards.length === 0 && !adding && (
        <div className="alert" style={{ marginTop: 0 }}>
          You don't have any saved cards. Saving one is optional — it just means you won't have to
          re-enter it on your next order.
        </div>
      )}

      {cards && cards.length > 0 && (
        <div className="text-sm" data-testid="saved-cards">
          {cards.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", padding: "0.35rem 0", borderBottom: "1px solid var(--light)" }}>
              <span style={{ fontWeight: 600 }}>{describeCard(c)}</span>
              {c.isDefault
                ? <span className="status-chip" style={{ background: "#EFF8F3", color: "var(--green)" }}>Default</span>
                : (
                  <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}
                    disabled={Boolean(busy)} onClick={() => act("set_default", c.id)}>Make default</button>
                )}
              <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}
                disabled={Boolean(busy)} onClick={() => act("remove", c.id)}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {!adding && cards !== null && (
        <button className="btn-primary" style={{ marginTop: "0.6rem", fontSize: "0.78rem", padding: "0.35rem 0.8rem" }}
          onClick={() => { setErr(""); setAdding(true); }}>Add payment method</button>
      )}

      {adding && (
        <AddCardForm accessToken={accessToken}
          onDone={async () => { setAdding(false); await load(); }}
          onCancel={() => setAdding(false)} />
      )}

      {err && <div className="alert alert-warn" style={{ marginTop: "0.5rem" }}>{err}</div>}
    </div>
  );
}

// The card form. Stripe's Element is mounted into the div below; every keystroke
// stays inside Stripe's iframe, and confirmCardSetup posts it to Stripe directly.
function AddCardForm({ accessToken, onDone, onCancel }) {
  const mountRef = useRef(null);
  const cardRef = useRef(null);
  const stripeRef = useRef(null);
  const [status, setStatus] = useState(stripeConfigured() ? "loading" : "unavailable");
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!stripeConfigured()) return undefined;
    (async () => {
      const stripe = await loadStripe();
      if (cancelled) return;
      if (!stripe) { setStatus("unavailable"); return; }
      stripeRef.current = stripe;
      const elements = stripe.elements();
      const card = elements.create("card", { hidePostalCode: false });
      card.mount(mountRef.current);
      cardRef.current = card;
      setStatus("ready");
    })();
    return () => {
      cancelled = true;
      if (cardRef.current) { try { cardRef.current.unmount(); } catch (e) { /* already gone */ } }
    };
  }, []);

  async function save() {
    if (status !== "ready") return;
    setStatus("saving");
    setErr("");
    try {
      // The client secret authorizes ONE card to be attached to the Stripe
      // Customer the server picked. It cannot charge anything.
      const res = await fetch("/.netlify/functions/client-payment-methods", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ action: "setup_intent" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.clientSecret) throw new Error(data.error || "The card form could not be started.");

      const result = await stripeRef.current.confirmCardSetup(data.clientSecret, {
        payment_method: { card: cardRef.current },
      });
      if (result.error) throw new Error(result.error.message || "That card could not be saved.");
      await onDone();
    } catch (e) {
      setErr((e && e.message) || "That card could not be saved.");
      setStatus("ready");
    }
  }

  if (status === "unavailable") {
    return (
      <div className="alert alert-warn" style={{ marginTop: "0.6rem" }}>
        Saving a card isn't available right now. You can still pay normally at checkout.
        <div style={{ marginTop: "0.4rem" }}>
          <button className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.2rem 0.6rem" }} onClick={onCancel}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.6rem", maxWidth: 420 }} data-testid="add-card-form">
      <div ref={mountRef} style={{ border: "1px solid var(--light)", borderRadius: 4, padding: "0.65rem", background: "var(--white)" }} />
      <div className="text-sm" style={{ color: "var(--gray)", marginTop: "0.35rem" }}>
        Your card details go directly to Stripe. We never see or store your card number.
      </div>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button className="btn-primary" style={{ fontSize: "0.78rem", padding: "0.35rem 0.8rem" }}
          disabled={status !== "ready"} onClick={save}>
          {status === "saving" ? "Saving…" : status === "loading" ? "Loading…" : "Save Card"}
        </button>
        <button className="btn-secondary" style={{ fontSize: "0.78rem", padding: "0.35rem 0.8rem" }}
          disabled={status === "saving"} onClick={onCancel}>Cancel</button>
      </div>
      {err && <div className="alert alert-warn" style={{ marginTop: "0.5rem" }}>{err}</div>}
    </div>
  );
}
